import axios from 'axios';

// Create axios instance with default configuration
// validateStatus: () => true — accept ALL HTTP status codes without throwing,
// matching the dev-v3 native fetch + handleResponse behaviour where the response
// body is always returned regardless of status code.
const axiosInstance = axios.create({
  baseURL: '', // Use relative paths for .NET integration
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: () => true,
});

// ── Page-unload cancellation ─────────────────────────────────────────────────
// Native fetch is automatically cancelled by the browser when the page unloads
// (reload / tab close / hard navigation). Axios uses XHR and does NOT do this,
// which leaves backend DB connections open indefinitely.
// We replicate fetch behaviour: track every in-flight request and abort them all
// the moment the page is being unloaded.
const pendingControllers = new Set();

window.addEventListener('pagehide', () => {
  pendingControllers.forEach(ctrl => ctrl.abort());
  pendingControllers.clear();
});
// ────────────────────────────────────────────────────────────────────────────

// Request interceptor - Handle JSON stringification
// NOTE: Auth is handled via session cookie + requestHeader_TKN/requestHeader_DT custom headers
// passed directly in CallApi. Do NOT add Authorization: Bearer here — the server uses
// cookie-based session auth and will silently reject requests with an unrecognised Bearer token.
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      // Handle pre-stringified JSON data from legacy code
      // If data is already a JSON string, parse it back to object for axios to handle
      if (typeof config.data === 'string' && config.data.length > 0) {
        try {
          config.data = JSON.parse(config.data);
        } catch (e) {
          // If parsing fails, it's not JSON, leave it as is
        }
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }

    // Attach a per-request AbortController so we can cancel on page unload.
    // If the caller already supplied a signal, respect it and don't override.
    if (!config.signal) {
      const controller = new AbortController();
      config.signal = controller.signal;
      pendingControllers.add(controller);

      // Remove from the tracking set once the request settles (avoids memory leak)
      config.signal.addEventListener('abort', () => pendingControllers.delete(controller));
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
// Because validateStatus: () => true is set, ALL responses (200, 401, 500, etc.)
// come through the success handler. Only genuine network errors (no response at all)
// go to the error handler.
axiosInstance.interceptors.response.use(
  (response) => {
    // Remove the controller from the pending set on successful response
    pendingControllers.forEach(ctrl => {
      if (ctrl.signal === response.config?.signal) pendingControllers.delete(ctrl);
    });

    // Handle 401 Unauthorized — clear auth and redirect to login
    if (response.status === 401) {
      localStorage.removeItem('ajs_user_id');
      localStorage.removeItem('_Host_Pro_Hckare_TKN');
      localStorage.removeItem('_Host_Pro_type');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return response;
  },
  (error) => {
    // Swallow intentional cancellation errors (page unload or caller-triggered abort)
    // — these are not real failures, the same way fetch silently stops on unload.
    if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
      // Return a resolved promise with empty data so callers don't need try/catch
      return Promise.resolve({ data: [] });
    }

    // Only fires for genuine network errors (server unreachable, CORS abort, etc.)
    if (!error.response) {
      console.error('Network error - no response received');
    }
    return Promise.reject(error);
  }
);

// Helper function for FormData requests
export const createFormDataRequest = (data) => {
  const formData = new FormData();
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      formData.append(key, data[key]);
    }
  });
  return formData;
};

// Helper for query string parameters
export const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== null && params[key] !== undefined) {
      searchParams.append(key, params[key]);
    }
  });
  return searchParams.toString();
};

export default axiosInstance;
